package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.InvoiceLayoutStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-013 creation for owners and standard members; UC-015 artefacts of an opened invoice that is
// not a Draft. `invoiceId` is null on the list page. The page title names the view
// (`invoiceTitle`); creation and the progress controls stay in the page
// (`docs/design/screens/06-Rechnung.png`).
@Composable
fun invoiceRecords(
    organisationId: String,
    invoiceId: String?,
    state: InvoiceUiState,
    actions: InvoiceActions,
) {
    val focusReturn = remember { FocusReturn() }
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.error?.authenticationRequired != true) {
        if (state.busy) invoiceStatus("Wird geladen oder berechnet …")
        state.notice?.let { invoiceStatus(it) }
        invoiceChanges(state.changes)
        invoiceProblems(state.problems)
        val current = state.current
        when {
            state.draft != null -> {
                invoiceFocus(state, focusReturn)
                invoiceCreation(state, actions)
            }
            current != null -> {
                invoiceFocus(state, focusReturn)
                if (invoiceId == null) {
                    Div(attrs = CardStackStyle.toAttrs()) { invoiceRecord(current) }
                    createdActions(organisationId, current, state, actions)
                } else {
                    Div(attrs = InvoiceLayoutStyle.toAttrs()) {
                        Div(attrs = CardStackStyle.toAttrs()) { invoiceRecord(current) }
                        Div(attrs = CardStackStyle.toAttrs()) {
                            invoiceProgression(
                                organisationId,
                                invoiceId,
                                actions.progressed,
                                actions.withdraw,
                            )
                            if (current.status != DRAFT)
                                issuedInvoiceArtefacts(organisationId, invoiceId, actions.withdraw)
                        }
                    }
                    reloadInvoice(state, actions)
                }
            }
            invoiceId == null -> invoiceList(organisationId, state, actions, focusReturn)
            else -> reloadInvoice(state, actions)
        }
    }
    if (invoiceId != null)
        P {
            A(href = "/organisations/$organisationId/invoices") {
                Text("Zurück zur Rechnungsliste")
            }
        }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

/** The page title of the shown view; the detail page keeps "Rechnung" until its invoice loads. */
fun invoiceTitle(invoiceId: String?, state: InvoiceUiState): String {
    val current = state.current
    return when {
        state.draft != null -> "Rechnung erstellen"
        current != null -> if (current.status == DRAFT) "Rechnungsentwurf" else "Rechnung"
        invoiceId == null -> INVOICES_TITLE
        else -> "Rechnung"
    }
}

// Each view calls this at its own place, so a new view focuses the page title again; while a
// problem is shown, the alert or the marked field keeps focus. The first render leaves the frame's
// title focus alone.
@Composable
internal fun invoiceFocus(state: InvoiceUiState, focusReturn: FocusReturn) {
    LaunchedEffect(Unit) {
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(INVOICE_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

@Composable
private fun invoiceStatus(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
private fun reloadInvoice(state: InvoiceUiState, actions: InvoiceActions) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            "Rechnung erneut laden",
            actions.refresh,
            variant = ButtonVariant.GHOST,
            disabled = state.busy,
        )
    }
}

// A5, A6: every rejected field with its correction; marked fields refer to this list.
@Composable
internal fun invoiceProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id("invoice-problems") }) {
        problems.forEach { (field, correction) ->
            Li { Text("${invoiceFieldLabel(field)}: $correction") }
        }
    }
}

// A3, A4: what the server changed in the refreshed positions.
@Composable
private fun invoiceChanges(changes: List<String>) {
    if (changes.isEmpty()) return
    Ul(
        attrs =
            HintStyle.toAttrs {
                id("invoice-changes")
                style {
                    property("margin", "0")
                    property("padding-left", "32px")
                }
            }
    ) {
        changes.forEach { Li { Text(it) } }
    }
}

// Steps 2-9: details, positions and the server's totals; creation needs calculated positions and
// no open position editor. Cancelling (A9) sends nothing.
@Composable
private fun invoiceCreation(state: InvoiceUiState, actions: InvoiceActions) {
    Div(attrs = CardStackStyle.toAttrs()) {
        invoiceDetailsForm(state, actions)
        invoiceReview(state, actions)
        if (state.line != null) invoiceLineForm(state, actions)
        Div(attrs = RowStyle.toAttrs()) {
            themedButton(
                "Rechnungsentwurf erstellen",
                actions.confirm,
                variant = ButtonVariant.PRIMARY,
                disabled = state.busy || state.preview == null || state.line != null,
            )
            themedButton("Abbrechen", actions.cancel, disabled = state.busy)
        }
    }
}

@Composable
private fun createdActions(
    organisationId: String,
    current: InvoiceResponse,
    state: InvoiceUiState,
    actions: InvoiceActions,
) {
    Div(attrs = RowStyle.toAttrs()) {
        linkButton(
            "Rechnungsentwurf öffnen",
            "/organisations/$organisationId/invoices/${current.id}",
            variant = ButtonVariant.PRIMARY,
        )
        themedButton("Zur Rechnungsliste", actions.close, disabled = state.busy)
    }
}

// The frame's page title; every invoice view focuses it.
internal const val INVOICE_TITLE_ID = "page-title"

/** Title of the list page and parent of every invoice breadcrumb. */
const val INVOICES_TITLE = "Rechnungen"
internal const val DRAFT = "DRAFT"
